"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_PRIORITY_BY_KIND = exports.AttendantRequestsUploadRequest = exports.ATTENDANT_REQUESTS_UPLOAD_MAX = exports.AttendantRequestUpload = exports.AttendantDecideRequest = exports.AttendantReleaseRequest = exports.AttendantClaimRequest = exports.AttendantRequestDetail = exports.AttendantRequestSummary = exports.AttendantRequestPayload = exports.GenericCallPayload = exports.RandomRecheckPayload = exports.ManualPricePayload = exports.SkipBaggingPayload = exports.UnknownItemPayload = exports.AgeRestrictedPayload = exports.WeightMismatchPayload = exports.AttendantDecisionOutcome = exports.AttendantRequestState = exports.AttendantRequestPriority = exports.AttendantRequestKind = void 0;
const zod_1 = require("zod");
const pricing_js_1 = require("./pricing.js");
/**
 * Attendant Console approvals contracts (Sprint 9 — TRS §7 + §6.12).
 *
 * SCO terminals author approval requests locally (weight mismatch /
 * age restriction / unknown item / skip-bagging / etc.) and upload
 * them to BOS via `/sync/approval-requests`. BOS persists them in
 * `attendant_requests` and exposes the legacy claim/decide REST fallback.
 * ADR-0010's primary live loop is LAN-direct; there is no BOS
 * WebSocket event contract.
 *
 * See `apps/bos/docs/modules/approvals.md` for the full spec.
 */
// ---------- Primitive shapes ----------
/**
 * Categories of approval an SCO can raise. Maps to TRS §6.12
 * priority table (per-kind priority is server-asserted on upload —
 * the terminal does NOT get to claim P1 priority on a `skip_bagging`).
 */
exports.AttendantRequestKind = zod_1.z.enum([
    'weight_mismatch',
    'age_restricted',
    'unknown_item',
    'skip_bagging',
    'manual_price',
    'random_recheck',
    'generic_call',
]);
/**
 * TRS §6.12 priority categories. P1 = highest. Sprint 9 stores
 * priority verbatim on every request; the per-priority backpressure
 * loop ships in Sprint 12.
 */
exports.AttendantRequestPriority = zod_1.z.enum(['P1', 'P2', 'P3', 'P4', 'P5']);
exports.AttendantRequestState = zod_1.z.enum([
    'pending',
    'claimed',
    'approved',
    'declined',
    'escalated',
    'expired',
]);
exports.AttendantDecisionOutcome = zod_1.z.enum(['approve', 'decline', 'escalate']);
// ---------- Payload-by-kind ----------
/**
 * Discriminated payload union. Server re-validates on persist and
 * rejects any payload whose shape doesn't match the kind. Each
 * branch is `.strict()` so unknown fields trip validation rather
 * than silently round-trip.
 */
exports.WeightMismatchPayload = zod_1.z
    .object({
    itemId: zod_1.z.string().min(1),
    expectedGrams: zod_1.z.number().int().positive(),
    observedGrams: zod_1.z.number().int().positive(),
    toleranceGrams: zod_1.z.number().int().min(0),
    severity: zod_1.z.enum(['mild', 'severe']),
})
    .strict();
exports.AgeRestrictedPayload = zod_1.z
    .object({
    itemId: zod_1.z.string().min(1),
    minimumAge: zod_1.z.number().int().min(1).max(150),
    ageCategoryId: zod_1.z.string().min(1),
})
    .strict();
exports.UnknownItemPayload = zod_1.z
    .object({
    scannedBarcode: zod_1.z.string().min(1).max(64),
    observedGrams: zod_1.z.number().int().positive().optional(),
})
    .strict();
exports.SkipBaggingPayload = zod_1.z
    .object({
    itemId: zod_1.z.string().min(1),
    reasonHint: zod_1.z.string().max(255).optional(),
})
    .strict();
exports.ManualPricePayload = zod_1.z
    .object({
    itemId: zod_1.z.string().min(1),
    requestedUnitPriceMinor: pricing_js_1.MoneyMinor,
    originalUnitPriceMinor: pricing_js_1.MoneyMinor,
})
    .strict();
exports.RandomRecheckPayload = zod_1.z
    .object({
    itemId: zod_1.z.string().min(1),
    expectedGrams: zod_1.z.number().int().positive(),
    observedGrams: zod_1.z.number().int().positive(),
})
    .strict();
exports.GenericCallPayload = zod_1.z
    .object({
    reasonHint: zod_1.z.string().max(255).optional(),
})
    .strict();
/**
 * Discriminated union linking `kind` to its `payload`. Used by the
 * sync upload schema below + the persisted-row read schema.
 */
exports.AttendantRequestPayload = zod_1.z.discriminatedUnion('kind', [
    zod_1.z.object({ kind: zod_1.z.literal('weight_mismatch'), payload: exports.WeightMismatchPayload }),
    zod_1.z.object({ kind: zod_1.z.literal('age_restricted'), payload: exports.AgeRestrictedPayload }),
    zod_1.z.object({ kind: zod_1.z.literal('unknown_item'), payload: exports.UnknownItemPayload }),
    zod_1.z.object({ kind: zod_1.z.literal('skip_bagging'), payload: exports.SkipBaggingPayload }),
    zod_1.z.object({ kind: zod_1.z.literal('manual_price'), payload: exports.ManualPricePayload }),
    zod_1.z.object({ kind: zod_1.z.literal('random_recheck'), payload: exports.RandomRecheckPayload }),
    zod_1.z.object({ kind: zod_1.z.literal('generic_call'), payload: exports.GenericCallPayload }),
]);
// ---------- Read shapes (BOS REST surface) ----------
/**
 * Wire shape returned by `GET /approvals` (list). Payload is validated by
 * the matching `AttendantRequestPayload` branch.
 */
exports.AttendantRequestSummary = zod_1.z.object({
    id: zod_1.z.string(),
    clientUuid: zod_1.z.string(),
    terminalId: zod_1.z.string(),
    kind: exports.AttendantRequestKind,
    priority: exports.AttendantRequestPriority,
    state: exports.AttendantRequestState,
    payload: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()),
    actorCashierId: zod_1.z.string().nullable(),
    itemId: zod_1.z.string().nullable(),
    claimedByUserId: zod_1.z.string().nullable(),
    claimedAt: zod_1.z.string().datetime().nullable(),
    decisionOutcome: exports.AttendantDecisionOutcome.nullable(),
    decisionByUserId: zod_1.z.string().nullable(),
    decisionAt: zod_1.z.string().datetime().nullable(),
    decisionNotes: zod_1.z.string().nullable(),
    requestedAt: zod_1.z.string().datetime(),
    expiresAt: zod_1.z.string().datetime().nullable(),
    lockVersion: zod_1.z.number().int().min(0),
    createdAt: zod_1.z.string().datetime(),
    updatedAt: zod_1.z.string().datetime(),
});
/**
 * `GET /approvals/:id` — admin variant with denormalised display
 * fields joined on. Sprint 9 ships the bare summary; richer joins
 * (terminal label, cashier display name, item name) are added in a
 * polish follow-up.
 */
exports.AttendantRequestDetail = exports.AttendantRequestSummary.extend({
    terminalLabel: zod_1.z.string().nullable(),
    cashierDisplayName: zod_1.z.string().nullable(),
    itemName: zod_1.z.string().nullable(),
    claimedByDisplayName: zod_1.z.string().nullable(),
    decisionByDisplayName: zod_1.z.string().nullable(),
});
// ---------- Write shapes (BOS REST surface) ----------
/**
 * `POST /approvals/:id/claim` body. Empty — identity comes from JWT.
 */
exports.AttendantClaimRequest = zod_1.z.object({}).strict();
/**
 * `POST /approvals/:id/release` body. Empty.
 */
exports.AttendantReleaseRequest = zod_1.z.object({}).strict();
/**
 * `POST /approvals/:id/decide` body. `outcome` required; `notes`
 * optional (≤ 1000 chars).
 */
exports.AttendantDecideRequest = zod_1.z
    .object({
    outcome: exports.AttendantDecisionOutcome,
    notes: zod_1.z.string().max(1000).optional(),
})
    .strict();
// ---------- Sync upload (terminal → BOS) ----------
/**
 * Single uploaded record. The shared `(kind, payload)` discriminator
 * is enforced via merge with `AttendantRequestPayload`.
 *
 * `clientUuid` is the idempotency key — replay is safe per the H-86
 * pattern. `priority` is asserted by the terminal but server may
 * normalise it on persist (TRS §6.12 maps `kind` to a default
 * priority; an SCO uploading `P1` for a `skip_bagging` is clamped
 * down server-side).
 */
const AttendantRequestUploadBase = zod_1.z.object({
    clientUuid: zod_1.z.string().min(1).max(64),
    priority: exports.AttendantRequestPriority,
    requestedAt: zod_1.z.string().datetime(),
    actorCashierId: zod_1.z.string().nullable(),
});
exports.AttendantRequestUpload = zod_1.z.discriminatedUnion('kind', [
    AttendantRequestUploadBase.extend({
        kind: zod_1.z.literal('weight_mismatch'),
        payload: exports.WeightMismatchPayload,
    }),
    AttendantRequestUploadBase.extend({
        kind: zod_1.z.literal('age_restricted'),
        payload: exports.AgeRestrictedPayload,
    }),
    AttendantRequestUploadBase.extend({
        kind: zod_1.z.literal('unknown_item'),
        payload: exports.UnknownItemPayload,
    }),
    AttendantRequestUploadBase.extend({
        kind: zod_1.z.literal('skip_bagging'),
        payload: exports.SkipBaggingPayload,
    }),
    AttendantRequestUploadBase.extend({
        kind: zod_1.z.literal('manual_price'),
        payload: exports.ManualPricePayload,
    }),
    AttendantRequestUploadBase.extend({
        kind: zod_1.z.literal('random_recheck'),
        payload: exports.RandomRecheckPayload,
    }),
    AttendantRequestUploadBase.extend({
        kind: zod_1.z.literal('generic_call'),
        payload: exports.GenericCallPayload,
    }),
]);
/**
 * Per the established sync pattern: capped at 1000 per batch.
 * SCO approvals are rare relative to transactions; the cap is well
 * above peak SCO emission rates while still bounding worst-case
 * upload size.
 */
exports.ATTENDANT_REQUESTS_UPLOAD_MAX = 1000;
exports.AttendantRequestsUploadRequest = zod_1.z.object({
    records: zod_1.z.array(exports.AttendantRequestUpload).min(1).max(exports.ATTENDANT_REQUESTS_UPLOAD_MAX),
});
// ---------- Default per-kind priority (server-asserted) ----------
/**
 * TRS §6.12 priority mapping. Server uses this to clamp terminal-
 * asserted priorities on upload — an SCO that claims P1 for a
 * `skip_bagging` request is normalised to P3 server-side. Kept here
 * (not server-side only) so the terminal can use the same mapping
 * to pre-validate before uploading.
 */
exports.DEFAULT_PRIORITY_BY_KIND = {
    age_restricted: 'P1',
    weight_mismatch: 'P2',
    unknown_item: 'P2',
    manual_price: 'P2',
    skip_bagging: 'P3',
    random_recheck: 'P4',
    generic_call: 'P5',
};
//# sourceMappingURL=approvals.js.map